import 'dayjs/locale/nl'

import { errorToast } from '@atabix/vue-ui'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { computed, ref } from 'vue'

import { type DayPreferenceDto, useDayPreferences, useOffices, useUpsertDayPreference } from '@/api/lunchQueries'
import { useSettingsStore } from '@/stores/settings'

dayjs.extend(isoWeek)
dayjs.locale('nl')

export type DayPreference = {
  date: string
  attending: boolean
  office_id: string | null
  office_name: string | null
  locked: boolean
  // False while the day is still only projected from the user's standard week. Those
  // rows may be regenerated, so the shown answer is not yet the user's own -- approving
  // the week is what takes ownership of them.
  confirmed: boolean
  emoji: string
}

export type WeekBlock = {
  weekNumber: number
  label: string
  isCurrent: boolean
  isNext: boolean
  cutoff: dayjs.Dayjs
  cutoffPassed: boolean
  // The five weekdays (Mon–Fri) — always non-empty, so days[0] is guaranteed to exist.
  days: [DayPreference, ...DayPreference[]]
  expanded: boolean
}

export type FeboRound = {
  id: string
  caterer: string
  scheduledFor: string
  cutoffAt: string
  ctaTitle: string
  ctaSubtitle: string
  itemsTotal: number
  itemsOrdered: number
}

type WeekConfig = {
  weekNumber: number
  label: string
  isCurrent: boolean
  isNext: boolean
  weekStart: dayjs.Dayjs
  dates: [string, string, string, string, string]
}

function buildWeekConfig(weekStart: dayjs.Dayjs, isCurrent: boolean, isNext: boolean): WeekConfig {
  return {
    weekNumber: weekStart.isoWeek(),
    label: isNext ? 'Volgende week' : isCurrent ? 'Deze week' : `Week ${weekStart.isoWeek()}`,
    isCurrent,
    isNext,
    weekStart,
    dates: [
      weekStart.format('YYYY-MM-DD'),
      weekStart.add(1, 'day').format('YYYY-MM-DD'),
      weekStart.add(2, 'day').format('YYYY-MM-DD'),
      weekStart.add(3, 'day').format('YYYY-MM-DD'),
      weekStart.add(4, 'day').format('YYYY-MM-DD'),
    ],
  }
}

// At the weekend the calendar week is over and locked, so the window starts at the coming week:
// that is the one whose Sunday 11:00 cutoff is still open, and it is still "next week" to people.
function buildAllWeeks(): WeekConfig[] {
  const isWeekend = dayjs().isoWeekday() > 5
  const monday = dayjs(getPlanningWeekStart())
  return [
    buildWeekConfig(monday, true, isWeekend),
    buildWeekConfig(monday.add(1, 'week'), false, !isWeekend),
    buildWeekConfig(monday.add(2, 'week'), false, false),
    buildWeekConfig(monday.add(3, 'week'), false, false),
    buildWeekConfig(monday.add(4, 'week'), false, false),
    buildWeekConfig(monday.add(5, 'week'), false, false),
  ]
}

// `expandedWeekNumbers` and `approvingWeekNumber` are intentionally shared singletons across
// every `useLunch()` call -- unlike `todayKey`/`weekConfigs` below, which are recomputed on
// each call so a tab left open across midnight or the weekend doesn't keep treating a stale
// date as "today". A week number surviving into a later recompute stays expanded; one that
// rolls out of the 6-week window is simply never looked up again.
const expandedWeekNumbers = ref<Set<number>>(new Set([dayjs(getPlanningWeekStart()).isoWeek()]))
// Which week is mid-approval, so its button can show progress while the other weeks' stay idle.
const approvingWeekNumber = ref<number | null>(null)

const feboRound = ref<FeboRound>({
  id: 'febo-friday',
  caterer: 'Febo Friday!',
  scheduledFor: dayjs().day(5).format('YYYY-MM-DD'),
  cutoffAt: dayjs().day(5).hour(11).minute(0).toISOString(),
  ctaTitle: 'Febo Friday!',
  ctaSubtitle: 'Geef je bestelling voor vrijdag 11:00 door',
  itemsTotal: 2,
  itemsOrdered: 0,
})

export type MenuItem = {
  id: string
  name: string
  description: string
  category: string
  priceCents: number
  emoji: string
  quantity: number
}

const feboMenu = ref<MenuItem[]>([
  {
    id: 'mi-1',
    name: 'Frikandel speciaal',
    description: 'Met mayonaise, ketchup, ui',
    category: 'Snacks',
    priceCents: 350,
    emoji: '🌭',
    quantity: 0,
  },
  { id: 'mi-2', name: 'Kroket', description: 'Klassieke rundvleeskroket', category: 'Snacks', priceCents: 320, emoji: '🥖', quantity: 0 },
  { id: 'mi-3', name: 'Kaassoufflé', description: 'Knapperig met smeltende kaas', category: 'Snacks', priceCents: 280, emoji: '🧀', quantity: 0 },
  { id: 'mi-4', name: 'Bamischijf', description: 'Krokante bami in een schijf', category: 'Snacks', priceCents: 320, emoji: '🍜', quantity: 0 },
  { id: 'mi-5', name: 'Friet groot', description: 'Klassieke Hollandse friet', category: 'Bijgerechten', priceCents: 450, emoji: '🍟', quantity: 0 },
  { id: 'mi-6', name: 'Patatje oorlog', description: 'Mayo, satésaus, ui', category: 'Bijgerechten', priceCents: 550, emoji: '🍟', quantity: 0 },
  { id: 'mi-7', name: 'Cola', description: '0.33L blikje', category: 'Drinken', priceCents: 250, emoji: '🥤', quantity: 0 },
  { id: 'mi-8', name: 'Spa Blauw', description: 'Mineraalwater, 0.33L', category: 'Drinken', priceCents: 200, emoji: '💧', quantity: 0 },
])

export function useLunch() {
  // Recomputed on every call rather than frozen at module load, so "today" and the derived
  // 6-week window stay correct for a tab left open across midnight or the weekend.
  const todayKey = dayjs().startOf('day')
  const weekConfigs = buildAllWeeks()

  const allDates = weekConfigs.flatMap((w) => w.dates)
  const range = { from: allDates[0]!, to: allDates.at(-1)! }

  const {
    data: dayPreferencesData,
    isError: dayPreferencesFailed,
    error: dayPreferencesError,
    isPending: dayPreferencesLoading,
  } = useDayPreferences(range)
  const { activeOffices: offices } = useOffices()
  const upsert = useUpsertDayPreference()
  const settingsStore = useSettingsStore()

  const preferenceByDate = computed<Map<string, DayPreferenceDto>>(() => {
    const map = new Map<string, DayPreferenceDto>()
    for (const p of dayPreferencesData.value ?? []) {
      const key = String(p.date).slice(0, 10)
      map.set(key, p)
    }
    return map
  })

  const toDayPreference = (date: string, cutoffPassed = false): DayPreference => {
    const pref = preferenceByDate.value.get(date)
    const isPast = dayjs(date).isBefore(todayKey)
    const attending = pref?.attending ?? false
    return {
      date,
      attending,
      office_id: pref?.office_id ?? null,
      office_name: pref?.office?.name ?? null,
      // A passed weekly cutoff locks the whole week, not just days already in the past,
      // so the "no longer editable" message and the controls can never disagree.
      locked: isPast || cutoffPassed,
      confirmed: pref?.origin === 'manual',
      emoji: attending ? '🥐' : '🏠',
    }
  }

  const weeks = computed<WeekBlock[]>(() =>
    weekConfigs.map((cfg): WeekBlock => {
      const cutoff = cfg.weekStart.subtract(1, 'day').hour(11).minute(0)
      const cutoffPassed = cutoff.isBefore(dayjs())
      const [firstDate, ...otherDates] = cfg.dates
      return {
        weekNumber: cfg.weekNumber,
        label: cfg.label,
        isCurrent: cfg.isCurrent,
        isNext: cfg.isNext,
        cutoff,
        cutoffPassed,
        days: [toDayPreference(firstDate, cutoffPassed), ...otherDates.map((date): DayPreference => toDayPreference(date, cutoffPassed))],
        expanded: expandedWeekNumbers.value.has(cfg.weekNumber),
      }
    }),
  )

  const currentWeek = computed(() => weeks.value.find((w) => w.isCurrent))
  const orderedItems = computed(() => feboMenu.value.filter((index) => index.quantity > 0))
  const orderedTotal = computed(() => orderedItems.value.reduce((sum, item) => sum + item.priceCents * item.quantity, 0))
  const itemsCount = computed(() => orderedItems.value.reduce((sum, item) => sum + item.quantity, 0))
  const groupedMenu = computed(() => {
    const map = new Map<string, MenuItem[]>()
    for (const item of feboMenu.value) {
      if (!map.has(item.category)) map.set(item.category, [])
      map.get(item.category)!.push(item)
    }
    return [...map.entries()].map(([category, items]) => ({ category, items }))
  })

  async function toggleAttendance(_weekNumber: number, date: string) {
    const day = weeks.value.flatMap((w) => w.days).find((d) => d.date === date)
    if (!day || day.locked) return
    const nextAttending = !day.attending
    try {
      await upsert.mutateAsync({
        date,
        attending: nextAttending,
        office_id: nextAttending ? (day.office_id ?? settingsStore.defaultOfficeId ?? offices.value[0]?.id ?? null) : null,
      })
    } catch (error) {
      errorToast(error)
    }
  }

  async function setLocation(_weekNumber: number, date: string, officeId: string) {
    const day = weeks.value.flatMap((w) => w.days).find((d) => d.date === date)
    if (!day || day.locked) return
    try {
      await upsert.mutateAsync({
        date,
        attending: true,
        office_id: officeId,
      })
    } catch (error) {
      errorToast(error)
    }
  }

  // Approving a week re-posts every day it still only projects from the standard week,
  // which flips those rows to `manual` server-side: the answer on screen becomes the
  // stored one instead of a guess that a later defaults change could overwrite.
  async function approveWeek(weekNumber: number): Promise<void> {
    const week = weeks.value.find((candidate): boolean => candidate.weekNumber === weekNumber)
    if (!week || approvingWeekNumber.value !== null) return
    const pendingDays = week.days.filter((day): boolean => !day.locked && !day.confirmed)
    if (pendingDays.length === 0) return

    approvingWeekNumber.value = weekNumber
    try {
      for (const day of pendingDays) {
        await upsert.mutateAsync({
          date: day.date,
          attending: day.attending,
          office_id: day.attending ? (day.office_id ?? settingsStore.defaultOfficeId ?? offices.value[0]?.id ?? null) : null,
        })
      }
    } catch (error) {
      errorToast(error)
    } finally {
      approvingWeekNumber.value = null
    }
  }

  function toggleWeek(weekNumber: number) {
    const next = new Set(expandedWeekNumbers.value)
    if (next.has(weekNumber)) {
      next.delete(weekNumber)
    } else {
      next.add(weekNumber)
    }
    expandedWeekNumbers.value = next
  }

  function increment(menuItemId: string) {
    const item = feboMenu.value.find((m) => m.id === menuItemId)
    if (item) item.quantity = Math.min(item.quantity + 1, 10)
  }

  function decrement(menuItemId: string) {
    const item = feboMenu.value.find((m) => m.id === menuItemId)
    if (item) item.quantity = Math.max(item.quantity - 1, 0)
  }

  function clearOrder() {
    for (const index of feboMenu.value) index.quantity = 0
  }

  function placeOrder() {
    feboRound.value.itemsOrdered = itemsCount.value
  }

  return {
    weeks,
    currentWeek,
    offices,
    // Surfaced so views can show that loading failed instead of rendering the
    // empty-state defaults, which look exactly like a real "at home all week" answer.
    dayPreferencesFailed,
    dayPreferencesError,
    // Views must not render the week from empty-state defaults while the real answer is
    // still in flight -- that reads as a confident "at home all week" and then flips.
    dayPreferencesLoading,
    feboRound,
    feboMenu,
    groupedMenu,
    orderedItems,
    orderedTotal,
    itemsCount,
    toggleAttendance,
    setLocation,
    approveWeek,
    approvingWeekNumber,
    toggleWeek,
    increment,
    decrement,
    clearOrder,
    placeOrder,
  }
}

export function isoWeekOf(dateString: string): number {
  return dayjs(dateString).isoWeek()
}

export function getCurrentWeekStart(): string {
  return dayjs().startOf('isoWeek').format('YYYY-MM-DD')
}

/** The week a planner looks at: the current one on workdays, the coming one at the weekend. */
export function getPlanningWeekStart(): string {
  const today = dayjs()
  const weekStart = today.startOf('isoWeek')
  return (today.isoWeekday() > 5 ? weekStart.add(1, 'week') : weekStart).format('YYYY-MM-DD')
}

/** Today when it falls in the given week, otherwise that week's Monday. */
export function getFocusDate(weekDates: string[]): string {
  const today = getToday()
  return weekDates.includes(today) ? today : (weekDates[0] ?? today)
}

export function getToday(): string {
  return dayjs().format('YYYY-MM-DD')
}

export function getWeekStartDates(weeksBackward: number, weeksForward: number): string[] {
  const currentMonday = dayjs().startOf('isoWeek')
  return Array.from({ length: weeksBackward + weeksForward + 1 }, (_, index): string =>
    currentMonday.add(index - weeksBackward, 'week').format('YYYY-MM-DD'),
  )
}

export function getWeekDates(weekStartDate: string): string[] {
  const weekStart = dayjs(weekStartDate)
  return Array.from({ length: 5 }, (_, index): string => weekStart.add(index, 'day').format('YYYY-MM-DD'))
}

export function getWeekDateRange(weekStartDate: string): { from: string; to: string } {
  const weekStart = dayjs(weekStartDate)
  return {
    from: weekStart.format('YYYY-MM-DD'),
    to: weekStart.add(4, 'day').format('YYYY-MM-DD'),
  }
}

export function formatWeekdayShort(date: string): string {
  return dayjs(date).format('dd D')
}

export function formatWeekdayAbbreviation(date: string): string {
  return dayjs(date).format('dd')
}

export function formatDayOfMonth(date: string): string {
  return dayjs(date).format('D')
}

export function formatWeekCutoff(cutoff: dayjs.Dayjs): string {
  return cutoff.format('dddd D MMM HH:mm')
}

export function formatDayLong(date: string): string {
  return dayjs(date).format('dddd D MMMM')
}

export function formatDateShort(date: string): string {
  return dayjs(date).format('D MMM')
}

export function formatPriceEuro(cents: number): string {
  return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`
}
